import React from 'react';
import { Alert, Button, Checkbox, Popconfirm, Table, Tag, message } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import moment from 'moment';
import MainContent from '@/layouts/MainContent';
import useHasPermissions from '@/hooks/useHasPermissions';
import { APP_PERMISSIONS } from '@/services/roleService';
import { DEFAULT_DISPLAY_DATE_TIME_FORMAT, formatDisplayDate } from '@/config';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { TariffAgeIssueModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'tariffAgeIssues_Title';

// Heures du départ en heure de la gare (envoyées sans fuseau) : affichées telles quelles.
const formatStation = (v?: string | Date) => (v ? moment.utc(v).format(DEFAULT_DISPLAY_DATE_TIME_FORMAT) : '');
const formatLocal = (v?: string | Date) => (v ? moment(v).format(DEFAULT_DISPLAY_DATE_TIME_FORMAT) : '');
const formatBirth = (v?: string) => formatDisplayDate(v);

/**
 * Ventes hors ligne (tablette, vente à bord) gardées malgré un tarif incohérent avec l'âge du passager (P4) : revue au
 * back-office. Corriger le billet passe par une modification de billet (changement de tarif) depuis l'app caissier.
 */
export default function TariffAgeIssuesPage() {
  const { t } = useTranslation();
  const [includeReviewed, setIncludeReviewed] = React.useState(false);
  const canReview = useHasPermissions([APP_PERMISSIONS.ManageTickets]);
  const [reviewing, setReviewing] = React.useState<string>();
  const { data, isLoading, refetch } = useQuery(['company_tariff_age_issues', includeReviewed], () =>
    apiSdk.TariffAgeIssueService.tariffAgeIssues({ includeReviewed }),
  );

  const review = (item: TariffAgeIssueModel) => {
    setReviewing(item.ticketId);
    apiSdk.TariffAgeIssueService.reviewed({ source: item.source!, ticketId: item.ticketId! })
      .then(response => {
        if (response.success) {
          message.success(t('tariffAgeIssues_Reviewed'));
          refetch();
        } else {
          defaultApiErrorHandle(response);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setReviewing(undefined));
  };

  const columns = [
    { title: t('tariffAgeIssues_Ticket'), dataIndex: 'ticketRefNo' },
    {
      title: t('tariffAgeIssues_Source'),
      dataIndex: 'source',
      render: (v: number) => <Tag>{t(v === 2 ? 'tariffAgeIssues_SourceOnBoard' : 'tariffAgeIssues_SourceTablet')}</Tag>,
    },
    { title: t('tariffAgeIssues_Passenger'), dataIndex: 'passengerName' },
    { title: t('tariffAgeIssues_BirthDate'), dataIndex: 'dateOfBirth', render: (v?: string) => formatBirth(v) || '—' },
    { title: t('tariffAgeIssues_Tariff'), dataIndex: 'tariffName' },
    { title: t('tariffAgeIssues_Departure'), dataIndex: 'departureLocal', render: formatStation },
    {
      title: t('tariffAgeIssues_Issue'),
      dataIndex: 'issue',
      render: (v: string) => <Tag color={v === 'TariffBirthDateRequired' ? 'gold' : 'red'}>{t(`tariffAgeIssues_${v}`)}</Tag>,
    },
    { title: t('tariffAgeIssues_SoldOn'), dataIndex: 'soldOn', render: formatLocal },
    {
      title: t('tariffAgeIssues_Review'),
      key: 'review',
      render: (_: any, item: TariffAgeIssueModel) =>
        item.reviewedOn ? (
          <span>{t('tariffAgeIssues_ReviewedBy', { name: item.reviewedBy || '—', date: formatLocal(item.reviewedOn) })}</span>
        ) : canReview ? (
          <Popconfirm
            title={t('tariffAgeIssues_ConfirmReview')}
            okText={t('tariffAgeIssues_MarkReviewed')}
            cancelText={t('cancel')}
            onConfirm={() => review(item)}
          >
            <Button size="small" icon={<CheckOutlined />} loading={reviewing === item.ticketId} data-testid={`review-${item.ticketRefNo}`}>
              {t('tariffAgeIssues_MarkReviewed')}
            </Button>
          </Popconfirm>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('tariffAgeIssues_Explanation')} />
      <Checkbox checked={includeReviewed} onChange={e => setIncludeReviewed(e.target.checked)} style={{ marginBottom: 12 }}>
        {t('tariffAgeIssues_IncludeReviewed')}
      </Checkbox>
      <Table<TariffAgeIssueModel>
        rowKey="ticketId"
        loading={isLoading}
        dataSource={data || []}
        columns={columns as any}
        pagination={{ pageSize: 50 }}
        locale={{ emptyText: t('tariffAgeIssues_None') }}
        bordered
        size="middle"
      />
    </MainContent>
  );
}
